// "Update my portrait" — and, afterwards, what that actually did.
//
// The old button was a shrug: tap, wait, screen looks identical, no idea whether Oreia found
// five things or nothing at all. Silence in that moment reads as "this feature is broken", and the
// fix is not to make the button say something reassuring — it is to make it say something true.
//
// So every run reports. When it found things, it names them. When it didn't, it says so plainly
// and gives the counted reason: how many entries it read, what is one sighting short of being a
// pattern, what is waiting on a quote it can point to. Nothing here is generated; every sentence
// below is assembled from numbers the server counted. If there is no reason we can count, we say
// only that nothing changed.

import { IconCheck, IconSliders } from '../components/Icons';
import type { AnalysisRun } from '../api/portrait';

export type UpdateState = 'idle' | 'running' | 'waiting' | 'cooldown' | 'failed';

/** "3 entries" / "1 entry" — small enough to inline, common enough to get wrong. */
function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * "Sage", "Sage and Muse", "Sage, Muse and 3 more" — names where naming stays readable, a count
 * where it doesn't, because a run that lights up the whole set shouldn't read as a wall of names.
 */
function list(names: string[]): string {
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names[0]}, ${names[1]} and ${names.length - 2} more`;
}

/** What the run found, as a list of phrases: ["2 new patterns", "1 new memory"]. */
export function foundPhrases(run: AnalysisRun): string[] {
  const parts: string[] = [];
  if (run.newPatterns > 0) parts.push(plural(run.newPatterns, 'new pattern'));
  if (run.newMemories > 0) parts.push(plural(run.newMemories, 'new observation'));
  if (run.newMeanings > 0) parts.push(plural(run.newMeanings, 'new reading'));
  if (run.newLinks > 0) parts.push(plural(run.newLinks, 'new connection'));
  // An archetype seen for the first time and one that moved are different events, and saying
  // "movement in Sage" about a first sighting overstates what happened.
  const appeared = run.movements.filter((m) => m.direction === 'new').map((m) => m.name);
  const shifted = run.movements.filter((m) => m.direction !== 'new').map((m) => m.name);
  if (appeared.length > 0) parts.push(`${list(appeared)} appeared`);
  if (shifted.length > 0) parts.push(`${list(shifted)} shifted`);
  return parts;
}

/**
 * The honest sentence for a run that found nothing.
 *
 * Ordered by which explanation makes the others irrelevant: a paused vault explains everything, a
 * locked one explains everything after that, and so on down to "nothing has repeated yet", which is
 * the ordinary and by far most common answer.
 */
export function nothingNewCopy(run: AnalysisRun): { title: string; body: string } {
  if (run.reasons.includes('paused')) {
    return {
      title: 'Nothing new — memory-building is paused.',
      body: 'Oreia read nothing and learned nothing this time. You can turn memory-building back on in Settings; everything already in your portrait is untouched.',
    };
  }
  if (run.reasons.includes('locked')) {
    return {
      title: "Nothing new — Oreia couldn't read your entries.",
      body: 'Your vault has no server-readable key, so the analysis had nothing to work from. Your entries are safe; there is just nothing this pass could see.',
    };
  }

  const read = run.entriesRead > 0
    ? `Read ${plural(run.entriesRead, 'new entry', 'new entries')}. `
    : 'Everything you have written is already in the portrait. ';

  if (run.reasons.includes('awaiting_quote')) {
    return {
      title: 'Nothing new yet.',
      body: `${read}Some patterns are waiting on a line Oreia can quote back to you — it won't say anything it can't point at in your own writing.`,
    };
  }
  if (run.nearing.length > 0) {
    return {
      title: 'Nothing new yet — but something is close.',
      body: `${read}Nothing has repeated often enough to become a pattern. These are the nearest:`,
    };
  }
  return {
    title: 'Nothing new this time.',
    body: `${read}Nothing has repeated often enough yet for Oreia to call it a pattern. That is normal — patterns need a few sightings before they mean anything.`,
  };
}

function buttonLabel(state: UpdateState, newEntryCount: number): string {
  if (state === 'running') return 'Reading your entries…';
  if (state === 'waiting') return 'Check again';
  if (newEntryCount > 0) return `Update portrait · ${plural(newEntryCount, 'new entry', 'new entries')}`;
  return 'Update portrait';
}

/** "just now" / "2 hours ago" — quiet provenance for the last run. */
export function relativeTime(iso: string | null, now: Date = new Date()): string | null {
  if (!iso) return null;
  const parsed = Date.parse(iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`);
  if (Number.isNaN(parsed)) return null;
  const mins = Math.round((now.getTime() - parsed) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${plural(mins, 'minute')} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${plural(hours, 'hour')} ago`;
  return `${plural(Math.round(hours / 24), 'day')} ago`;
}

export function UpdatePanel({
  state,
  run,
  newEntryCount,
  onUpdate,
}: {
  state: UpdateState;
  run: AnalysisRun | null;
  newEntryCount: number;
  onUpdate(): void;
}) {
  const lastRun = run && run.status === 'done' ? relativeTime(run.finishedAt) : null;

  return (
    <section className="aupdate" aria-label="Update your portrait">
      <div className="aupdate-head">
        <button
          type="button"
          className="aupdate-btn"
          onClick={onUpdate}
          disabled={state === 'running'}
        >
          <IconSliders />
          {buttonLabel(state, newEntryCount)}
        </button>
        {lastRun ? <span className="aupdate-when">last updated {lastRun}</span> : null}
      </div>

      {state === 'cooldown' ? (
        <div className="aupdate-result">
          <p className="rt">Just a moment.</p>
          <p className="rb">Oreia is still working through the last request.</p>
        </div>
      ) : null}

      {state === 'failed' ? (
        <div className="aupdate-result">
          <p className="rt">Couldn&apos;t start that.</p>
          <p className="rb">Nothing was lost — try again shortly.</p>
        </div>
      ) : null}

      {state === 'running' ? (
        <div className="aupdate-result">
          <p className="rb" style={{ margin: 0 }}>
            Reading everything you have written since the last pass. This takes a moment.
          </p>
        </div>
      ) : null}

      {/* A long pass is not a failure, and it is certainly not "nothing happened" — which is what
          quietly returning to idle used to imply. */}
      {state === 'waiting' ? (
        <div className="aupdate-result" role="status">
          <p className="rt">Still reading.</p>
          <p className="rb">
            This pass is taking longer than usual. It finishes on the server whether or not you wait
            here, and the result will be on this screen when you come back.
          </p>
        </div>
      ) : null}

      {state === 'idle' && run && run.status === 'done' ? <RunResult run={run} /> : null}
    </section>
  );
}

function RunResult({ run }: { run: AnalysisRun }) {
  if (run.foundSomething) {
    const phrases = foundPhrases(run);
    return (
      <div className="aupdate-result" role="status">
        <p className="rt">
          <IconCheck style={{ width: 16, height: 16, marginRight: 6, verticalAlign: -2 }} />
          {phrases.join(' · ')}
        </p>
        <p className="rb">
          {run.entriesRead > 0
            ? `From ${plural(run.entriesRead, 'entry', 'entries')} read this pass. Everything new is on this page, with the entries it came from.`
            : 'Everything new is on this page, with the entries it came from.'}
        </p>
      </div>
    );
  }

  const copy = nothingNewCopy(run);
  return (
    <div className="aupdate-result" role="status">
      <p className="rt">{copy.title}</p>
      <p className="rb">{copy.body}</p>
      {run.nearing.length > 0 ? (
        <div className="aupdate-near">
          {run.nearing.map((n) => (
            <span className="nchip" key={`${n.type}-${n.label}`}>
              <b>{n.label}</b>
              <span className="nc">
                {n.have} of {n.need}
              </span>
            </span>
          ))}
        </div>
      ) : null}
      {run.pendingEntries > 0 ? (
        <p className="rb">
          {plural(run.pendingEntries, 'entry', 'entries')} still queued — update again to keep going.
        </p>
      ) : null}
    </div>
  );
}
